// Chat handling, message sending, history loading, and response rendering.

import {
  sessionId,
  documentId,
  cachedTree,
  suggestEdits,
  chatMode,
  isBusy,
  pinnedSelection,
  currentSelection,
  chatHistoryOffset,
  setPinnedSelection,
  setCurrentSelection,
  setSelectionMeta,
  setChatHistoryOffset,
  setBusy,
} from "./state";
import { chat as chatAPI, getChatHistory } from "../core/apiClient";
import {
  addMessage,
  renderMarkdown,
  _cleanAIResponse,
  _makeCopyBtn,
  _timeStr,
  devLog,
  updateEmptyChat,
  _labels,
} from "./messages";
import { _buildClauseMap, injectInlineClauseLinks } from "./clauseLinks";
import { renderSuggestedEdits } from "./edits";
import { trackSelection, pinSelection, locateNodes, locateByText, clearContext } from "./selection";

// All messages loaded into the main chat this session (raw API objects, persisted until page unload)
const _sessionMessages = [];

// Ordered narrative — plays sequentially every 8s, no loop. "Almost there" anchors at 2m+.
const _THINKING_PHRASES = [
  "Reviewing your query",
  "Understanding the requirements",
  "Identifying what matters",
  "Analysing the details",
  "Extracting key insights",
  "Evaluating the information",
  "Examining the nuances",
  "Connecting the key points",
  "Building the analysis",
  "Structuring the response",
  "Drafting your answer",
  "Refining the analysis",
  "Verifying the details",
  "Formulating the response",
  "Preparing your answer",
  "Almost there",
];

function _getFriendlyError(msg) {
  if (!msg) return "Something went wrong. Please try again.";
  const m = msg.toLowerCase();
  if (m.includes("timeout") || m.includes("timed out") || m.includes("aborted"))
    return "This is taking longer than expected. Please try again.";
  if (m.includes("network") || m.includes("load failed") || m.includes("failed to fetch"))
    return "Something went wrong. Please check your connection and try again.";
  if (m.includes("session expired") || m.includes("sign in"))
    return "Your session has expired. Please sign in again.";
  if (m.includes("401") || m.includes("unauthorized"))
    return "Your session has expired. Please sign in again.";
  if (m.includes("insufficient_credits") || m.includes("402"))
    return "You've run out of credits. Please top up to continue.";
  return "Something went wrong. Please try again.";
}

export function getSessionMessages() { return _sessionMessages; }
export function getSessionMessageCount() { return _sessionMessages.length; }

// Build a compact "@text…" reference pill for user messages
function _makeCtxPill(context) {
  if (!context || !context.selectionText || !context.selectionText.trim()) return null;

  const pill = document.createElement("div");
  pill.className = "msg-ctx-pill";

  const t = context.selectionText.trim();
  const truncated = t.length > 32 ? t.slice(0, 32) + "…" : t;
  const label = `@${truncated}`;

  const span = document.createElement("span");
  span.className = "ctx-pill-label";
  span.textContent = label;
  pill.appendChild(span);

  const capturedNodes = context.nodes ? context.nodes.map((n) => ({ ...n })) : [];
  const capturedText = context.selectionText || "";
  pill.addEventListener("click", async () => {
    let found = false;
    if (capturedNodes.length > 0) {
      found = await locateNodes({ nodes: capturedNodes, selectionText: capturedText });
    }
    if (!found && capturedNodes.length > 0) found = await locateByText(capturedNodes[0].text);
    if (!found && capturedText) await locateByText(capturedText);
  });

  return pill;
}

// Parse a timestamp string, treating bare ISO strings (no tz suffix) as UTC
function _parseTs(ts) {
  if (!ts) return null;
  // If no timezone indicator, append Z so JS parses as UTC not local
  if (typeof ts === "string" && !ts.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(ts)) {
    ts = ts + "Z";
  }
  return new Date(ts);
}

// Format a timestamp into a readable date label
export function _formatDateLabel(timestamp) {
  if (!timestamp) return "Today";
  const d = _parseTs(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

// Create and return a date separator element
function _makeDateSep(label) {
  const sep = document.createElement("div");
  sep.className = "date-separator";
  sep.dataset.date = label;
  sep.innerHTML =
    `<div class="date-sep-line"></div>` +
    `<span class="date-sep-label">${label}</span>` +
    `<div class="date-sep-line"></div>`;
  return sep;
}

// Add a "Today" separator before the first live message if not already present
export function ensureTodaySeparator() {
  const chatEl = document.getElementById("chat-area");
  if (!chatEl) return;
  const existing = chatEl.querySelector('[data-date="Today"]');
  if (!existing) {
    chatEl.appendChild(_makeDateSep("Today"));
  }
}

// Show a user message with compact selection pill above body
function addUserMessageWithContext(userQuery, context) {
  const chatEl = document.getElementById("chat-area");

  const wrapper = document.createElement("div");
  wrapper.className = "msg user";

  const label = document.createElement("div");
  label.className = "msg-label";
  label.textContent = _labels.user || "You";

  const body = document.createElement("div");
  body.className = "msg-body";
  body.textContent = userQuery;

  const time = document.createElement("div");
  time.className = "msg-time";
  time.textContent = _timeStr();

  const footer = document.createElement("div");
  footer.className = "msg-footer-row";
  footer.appendChild(time);

  wrapper.appendChild(label);

  const pill = _makeCtxPill(context);
  if (pill) wrapper.appendChild(pill);

  wrapper.appendChild(body);
  wrapper.appendChild(footer);

  chatEl.appendChild(wrapper);
  chatEl.scrollTop = chatEl.scrollHeight;

  return wrapper;
}

// Replace last AI message body with rendered response + clause links
function replaceLastAI(text, clauseMap, referencedClauses) {
  const chatEl = document.getElementById("chat-area");
  const msgs = chatEl.querySelectorAll(".msg.ai");
  if (msgs.length === 0) return;

  const last = msgs[msgs.length - 1];
  const body = last.querySelector(".msg-body");
  if (body) {
    body.innerHTML = renderMarkdown(_cleanAIResponse(text));
    body.classList.remove("thinking");
    injectInlineClauseLinks(body, clauseMap, referencedClauses);
  }

  if (!last.querySelector(".copy-response-btn")) {
    const copyBtn = _makeCopyBtn(text);
    const footerRow = last.querySelector(".msg-footer-row");
    if (footerRow) {
      footerRow.appendChild(copyBtn); // time is already first child
    } else {
      last.appendChild(copyBtn);
    }
  }

  const time = last.querySelector(".msg-time");
  if (time) time.textContent = _timeStr();
}

// "Get Selection" button — reads Word selection and pins it
export async function handleGetSelection() {
  await trackSelection();
  if (currentSelection) {
    await pinSelection();
    if (pinnedSelection && pinnedSelection.nodes && pinnedSelection.nodes.length > 0) {
      await locateNodes(pinnedSelection);
    }
  }
}

export function handleAsk() {
  const input = document.getElementById("user-input");
  const message = input.value.trim();
  if (!message) return;
  input.value = "";

  if (pinnedSelection) {
    handleChat(message, pinnedSelection);
  } else if (currentSelection && currentSelection.trim().length > 0) {
    handleChat(message, { selectionText: currentSelection, nodeIds: [] });
  } else {
    handleChat(message, null);
  }
}

// Send chat query to backend, render response with clause links + edit cards
export async function handleChat(userQuery, context) {
  if (isBusy) return;
  setBusy(true);

  if (!sessionId || !documentId) {
    addMessage("ai", "Still setting up. Please wait a moment and try again.");
    setBusy(false);
    return;
  }

  let selection = null;
  if (context && context.nodeIds && context.nodeIds.length > 0) {
    const nodeTexts = {};
    if (context.nodes) {
      for (const n of context.nodes) {
        nodeTexts[n.id] = n.text || "";
      }
    }
    selection = {
      node_ids: context.nodeIds,
      node_texts: Object.keys(nodeTexts).length > 0 ? nodeTexts : null,
      text: context.selectionText || "",
    };
  }

  ensureTodaySeparator();
  addUserMessageWithContext(userQuery, context);

  if (pinnedSelection) {
    setPinnedSelection(null);
    setCurrentSelection("");
    setSelectionMeta(null);
    clearContext();
  }

  const phrases = _THINKING_PHRASES;
  let currentPhrase = phrases[0];

  const aiMsg = addMessage("ai", `${currentPhrase}…`);
  const aiBody = aiMsg.querySelector(".msg-body");
  aiBody.classList.add("thinking");

  const startTime = Date.now();
  const pulseInterval = setInterval(() => {
    if (!aiBody.classList.contains("thinking")) return;
    const elapsed = Math.round((Date.now() - startTime) / 1000);
    const phraseIdx = Math.min(Math.floor(elapsed / 8), phrases.length - 1);
    const newPhrase = phrases[phraseIdx];
    const timeStr = elapsed < 60
      ? `${elapsed}s`
      : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
    const newText = elapsed === 0 ? `${newPhrase}…` : `${newPhrase}… (${timeStr})`;

    if (newPhrase !== currentPhrase) {
      aiBody.textContent = newText;
      currentPhrase = newPhrase;
    } else {
      // Same phrase, just update the timer quietly
      aiBody.textContent = newText;
    }
  }, 1000);

  try {
    const res = await chatAPI(sessionId, documentId, userQuery, selection, suggestEdits, chatMode);
    // devLog("CHAT", res);

    const clauseMap = _buildClauseMap(cachedTree || []);
    replaceLastAI(res.response, clauseMap, res.referenced_node_ids || res.referenced_clauses || []);

    if (res.suggested_edits && res.suggested_edits.length > 0) {
      renderSuggestedEdits(aiMsg, res.suggested_edits);
    }
  } catch (err) {
    // devLog("CHAT_ERR", err.message);
    replaceLastAI(_getFriendlyError(err.message));
  } finally {
    clearInterval(pulseInterval);
    setBusy(false);
  }
}

// Load previous chat messages (last 5 pairs, with "Load more" for older)
export async function loadChatHistory(isLoadMore = false) {
  if (!documentId) return;

  try {
    const history = await getChatHistory(documentId, 15, chatHistoryOffset);
    // devLog("CHAT_HISTORY", {
    //   total: history.total,
    //   returned: history.messages.length,
    //   has_more: history.has_more,
    // });

    if (history.messages.length === 0) {
      updateEmptyChat();
      return;
    }

    const chatEl = document.getElementById("chat-area");

    // Remove existing load-more button before rebuilding
    const existingBtn = chatEl.querySelector(".load-more-btn");
    if (existingBtn) existingBtn.remove();

    const historyClauseMap = _buildClauseMap(cachedTree || []);

    // Sort oldest-first so chat reads top-to-bottom chronologically
    history.messages.sort((a, b) => {
      if (a.timestamp && b.timestamp) return _parseTs(a.timestamp) - _parseTs(b.timestamp);
      return 0;
    });

    // Accumulate into session message store for search (deduplicate by timestamp+role)
    for (const msg of history.messages) {
      const dup = _sessionMessages.some(
        (m) => m.timestamp === msg.timestamp && m.role === msg.role
      );
      if (!dup) _sessionMessages.push(msg);
    }

    // Build the entire batch into a fragment so order is always correct:
    // [load-more-btn?] → [date-sep → messages] (oldest first)
    const frag = document.createDocumentFragment();

    if (history.has_more) {
      const loadMoreBtn = document.createElement("button");
      loadMoreBtn.className = "load-more-btn";
      loadMoreBtn.innerHTML =
        `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">` +
        `<path d="M8 12V4M4 8l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>` +
        `</svg>Load earlier messages`;
      loadMoreBtn.addEventListener("click", () => {
        setChatHistoryOffset(chatHistoryOffset + history.messages.length);
        loadChatHistory(true);
      });
      frag.appendChild(loadMoreBtn);
    }

    let lastDateLabel = null;

    history.messages.forEach((msg) => {
      const isAI = msg.role === "assistant";

      // Inject date separator when date changes
      const dateLabel = _formatDateLabel(msg.timestamp);
      if (dateLabel !== lastDateLabel) {
        lastDateLabel = dateLabel;
        frag.appendChild(_makeDateSep(dateLabel));
      }

      const wrapper = document.createElement("div");
      wrapper.className = `msg ${isAI ? "ai" : "user"} history`;

      const label = document.createElement("div");
      label.className = "msg-label";
      label.textContent = isAI ? "JEWEL" : (_labels.user || "You");

      const body = document.createElement("div");
      body.className = "msg-body";

      const time = document.createElement("div");
      time.className = "msg-time";
      if (msg.timestamp) {
        const d = _parseTs(msg.timestamp);
        const h = d.getHours();
        const m = String(d.getMinutes()).padStart(2, "0");
        const ampm = h >= 12 ? "PM" : "AM";
        time.textContent = `${h % 12 || 12}:${m} ${ampm}`;
      } else {
        time.textContent = "";
      }

      const footer = document.createElement("div");
      footer.className = "msg-footer-row";
      footer.appendChild(time);

      wrapper.appendChild(label);

      if (isAI) {
        body.innerHTML = renderMarkdown(_cleanAIResponse(msg.content));
        if (historyClauseMap && Object.keys(historyClauseMap).length > 0) {
          injectInlineClauseLinks(body, historyClauseMap);
        }
        wrapper.appendChild(body);
        footer.appendChild(_makeCopyBtn(msg.content));
      } else {
        // Extract selection text from msg.selection_text or legacy prefix
        let displayText = msg.content;
        let refText = msg.selection_text || null;

        if (!refText) {
          const selMatch = displayText.match(
            /^\[Selected text: "([\s\S]*?)"\](?:\n\[Node IDs: ([\s\S]*?)\])?\n\n([\s\S]*)$/
          );
          if (selMatch) {
            refText = selMatch[1];
            displayText = selMatch[3];
          }
        }

        // Compact pill for history user messages
        if (refText && refText.trim()) {
          const pill = document.createElement("div");
          pill.className = "msg-ctx-pill";
          const rt = refText.trim();
          const span = document.createElement("span");
          span.className = "ctx-pill-label";
          span.textContent = "@" + (rt.length > 32 ? rt.slice(0, 32) + "…" : rt);
          pill.appendChild(span);
          const capturedText = refText;
          pill.addEventListener("click", () => locateByText(capturedText));
          wrapper.appendChild(pill);
        }

        body.textContent = displayText;
        wrapper.appendChild(body);
      }

      wrapper.appendChild(footer);
      frag.appendChild(wrapper);
    });

    // Insert the entire fragment at the top of chat (after #chat-empty),
    // before any existing live messages — for both initial load and load-more.
    const firstMsg = chatEl.querySelector(".msg, .date-separator, .load-more-btn");
    if (firstMsg) {
      chatEl.insertBefore(frag, firstMsg);
    } else {
      chatEl.appendChild(frag);
    }

    if (!isLoadMore) {
      chatEl.scrollTop = chatEl.scrollHeight;
    }

    updateEmptyChat();
  } catch (err) {
    // devLog("CHAT_HISTORY_ERR", err.message);
    updateEmptyChat();
  }
}
